import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './index.css';
import { HomePage } from './pages/HomePage';
import { ApplyPage } from './pages/ApplyPage';
import { DashboardPage } from './pages/DashboardPage';
import { SettingsPage } from './pages/SettingsPage';
import { GraduateRolesPage } from './pages/GraduateRolesPage';
import { GraduateRolesRedirectPage } from './pages/GraduateRolesRedirectPage';
import { CallbackPage } from './pages/CallbackPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { UserTypeSelectionPage } from './pages/UserTypeSelectionPage';
import { LoginSelectionPage } from './pages/LoginSelectionPage';
import { CompanySignupPage } from './pages/CompanySignupPage';
import { CompanyLoginPage } from './pages/CompanyLoginPage';
import { CompanyOnboardingPage } from './pages/CompanyOnboardingPage';
import { CompanyJobPostingsPage } from './pages/CompanyJobPostingsPage';
import { HirerCandidatesPage } from './pages/HirerCandidatesPage';
import { PurchasedCandidatesPage } from './pages/PurchasedCandidatesPage';
import { HowMatchingWorksPage } from './pages/HowMatchingWorksPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsPage } from './pages/TermsPage';
import { Analytics } from "@vercel/analytics/react";
import { AuthWrapper } from './components/common/AuthWrapper';
import { useAuth } from './context/AuthContext';

function App() {
  return (
    <AuthWrapper>
      <Router>
        <ProtectedRoutes />
      </Router>
    </AuthWrapper>
  );
}

// Component that handles route protection (inside Router context)
const ProtectedRoutes: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Public routes that don't require authentication
  const publicRoutes = [
    '/',
    '/login',
    '/login-selection',
    '/signup',
    '/signup-selection',
    '/onboarding',
    '/apply',
    '/hiring-signup',
    '/hiring-login',
    '/hiring-onboarding',
    '/graduate-roles-list',
    // Legacy routes (redirected)
    '/company-signup',
    '/company-login',
    '/company-onboarding',
    '/privacy',
    '/terms'
  ];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading...</h2>
        </div>
      </div>
    );
  }

  // Redirect to landing page if not authenticated and trying to access protected route
  if (!user && !isPublicRoute) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Analytics />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/login-selection" element={<LoginSelectionPage />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-selection" element={<UserTypeSelectionPage />} />

        {/* Hiring Routes (new naming) */}
        <Route path="/hiring-signup" element={<CompanySignupPage />} />
        <Route path="/hiring-login" element={<CompanyLoginPage />} />
        <Route path="/hiring-onboarding" element={<CompanyOnboardingPage />} />

        {/* Legacy Hiring Routes (redirect to new names) */}
        <Route path="/company-signup" element={<Navigate to="/hiring-signup" replace />} />
        <Route path="/company-login" element={<Navigate to="/hiring-login" replace />} />
        <Route path="/company-onboarding" element={<Navigate to="/hiring-onboarding" replace />} />

        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/graduate-roles" element={<GraduateRolesPage />} />
        <Route path="/graduate-roles-list" element={<GraduateRolesRedirectPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/job-postings"
          element={
            user?.user_metadata?.user_type === 'company' || user?.user_metadata?.user_type === 'hirer'
              ? <CompanyJobPostingsPage />
              : <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="/candidates"
          element={
            user?.user_metadata?.user_type === 'company' || user?.user_metadata?.user_type === 'hirer'
              ? <HirerCandidatesPage />
              : <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="/how-matching-works"
          element={
            user?.user_metadata?.user_type === 'company' || user?.user_metadata?.user_type === 'hirer'
              ? <HowMatchingWorksPage />
              : <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="/candidate-details"
          element={
            user?.user_metadata?.user_type === 'company' || user?.user_metadata?.user_type === 'hirer'
              ? <PurchasedCandidatesPage />
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Redirect old routes to dashboard */}
        <Route path="/select-service" element={<Navigate to="/dashboard" replace />} />
        <Route path="/process" element={<Navigate to="/dashboard" replace />} />
        <Route path="/job-search" element={<Navigate to="/dashboard" replace />} />
        <Route path="/enhance" element={<Navigate to="/dashboard" replace />} />
        <Route path="/cover-letter" element={<Navigate to="/dashboard" replace />} />
        <Route path="/ats-score" element={<Navigate to="/dashboard" replace />} />

        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
};

export default App;
